import { useEffect, useRef, useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Sparkles, Send, X, Loader2, MessageCircle, UserCircle, RotateCcw } from 'lucide-react'
import api from '../lib/api'

interface Msg {
  role: 'user' | 'assistant'
  content: string
  intent?: string
  needsHuman?: boolean
}

interface ChatResponse {
  reply: string
  intent: string
  sessionId: string
  data?: unknown
  needsHuman?: boolean
}

export function AssistantChat() {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<Msg[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const send = useMutation({
    mutationFn: async (message: string) => {
      const { data } = await api.post('/assistant/chat', {
        message,
        sessionId,
      })
      return data as ChatResponse
    },
    onSuccess: (data) => {
      setSessionId(data.sessionId)
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.reply,
          intent: data.intent,
          needsHuman: data.needsHuman,
        },
      ])
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Lo siento, no he podido procesar tu petición. Inténtalo de nuevo.',
          intent: 'error',
          needsHuman: true,
        },
      ])
    },
  })

  useEffect(() => {
    if (!open) return
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [open])

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [messages, send.isPending])

  const handleSend = useCallback(() => {
    const trimmed = input.trim()
    if (!trimmed || send.isPending) return
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }])
    setInput('')
    send.mutate(trimmed)
  }, [input, send])

  const handleSuggestion = useCallback(
    (text: string) => {
      if (send.isPending) return
      setMessages((prev) => [...prev, { role: 'user', content: text }])
      send.mutate(text)
    },
    [send],
  )

  const handleNewChat = useCallback(() => {
    setMessages([])
    setSessionId(null)
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  const suggestions = [
    '¿Qué citas tengo hoy?',
    'Presupuestos pendientes',
    'Impagos vencidos',
    'Resumen del día',
  ]

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-5 right-5 z-50 group flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-br from-indigo-600 to-primary-600 text-white shadow-lg hover:shadow-xl active:scale-95 transition-all ${open ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        aria-label="Abrir asistente"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium hidden sm:inline">Asistente</span>
      </button>

      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-96 h-[32rem] max-h-[80vh] bg-surface-1 border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gradient-to-br from-indigo-50 to-primary-50">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-text-primary">Asistente</p>
                <p className="text-[10px] text-text-tertiary leading-tight">
                  Consulta agenda, balance, pacientes
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={handleNewChat}
                  className="p-1 rounded hover:bg-white/50 text-text-tertiary"
                  title="Nueva conversación"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-white/50 text-text-tertiary"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 ? (
              <div className="py-2">
                <p className="text-sm text-text-secondary text-center mb-3">
                  Hola, soy tu asistente. ¿Qué necesitas?
                </p>
                <div className="space-y-1.5">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSuggestion(s)}
                      className="block w-full text-left px-3 py-2 text-sm text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                    >
                      <MessageCircle className="w-3 h-3 inline mr-1.5" />
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
                      m.role === 'user'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-indigo-600 text-white'
                    }`}
                  >
                    {m.role === 'user' ? (
                      <UserCircle className="w-4 h-4" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5" />
                    )}
                  </div>
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                      m.role === 'user'
                        ? 'bg-primary-600 text-white rounded-tr-sm'
                        : m.needsHuman
                          ? 'bg-warning-50 text-warning-800 border border-warning-100 rounded-tl-sm'
                          : 'bg-surface-2 text-text-primary rounded-tl-sm'
                    }`}
                  >
                    <AssistantContent content={m.content} />
                  </div>
                </div>
              ))
            )}
            {send.isPending && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5" />
                </div>
                <div className="bg-surface-2 px-3 py-2 rounded-xl rounded-tl-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-text-tertiary" />
                    <span className="text-xs text-text-tertiary">Consultando...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border p-3 bg-surface-0">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSend()
              }}
              className="flex items-center gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu consulta..."
                className="flex-1 px-3 py-2 bg-surface-1 border border-border rounded-lg text-sm text-text-primary outline-none focus:border-indigo-400"
                disabled={send.isPending}
              />
              <button
                type="submit"
                disabled={!input.trim() || send.isPending}
                className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-700 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

function AssistantContent({ content }: { content: string }) {
  const lines = content.split('\n')

  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (line.startsWith('· ') || line.startsWith('- ')) {
          return (
            <div key={i} className="flex gap-1.5 items-start">
              <span className="text-indigo-400 mt-0.5 flex-shrink-0">•</span>
              <span>{formatLine(line.slice(2))}</span>
            </div>
          )
        }
        if (line.match(/^\d+\.\s/)) {
          return (
            <div key={i} className="flex gap-1.5 items-start">
              <span className="text-indigo-400 font-medium flex-shrink-0">
                {line.match(/^(\d+)\./)?.[1]}.
              </span>
              <span>{formatLine(line.replace(/^\d+\.\s/, ''))}</span>
            </div>
          )
        }
        if (!line.trim()) return <div key={i} className="h-1" />
        return <p key={i}>{formatLine(line)}</p>
      })}
    </div>
  )
}

function formatLine(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return part
  })
}
